import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getSettings, getPreferences } from '@/services/account'
import { useOnboardingStore } from '@/stores/onboarding'
import { useCustomerIo } from '@/composables/useCustomerIo'
import { Sentry, setSentryUser, clearSentryUser } from '@/services/sentry'

// Empty preferences structure - used as default/reset value
const EMPTY_PREFERENCES = {
  markets: [],
  bet_types: [],
  bet_timing: [],
  risk: [],
  content: [],
  sports: [],
}

export const useUserStore = defineStore('user', () => {
  // State - Settings
  const settings = ref({
    username: null,
    id: null,
    email: null,
    country: null,
    oddsFormat: null,
    timezone: null,
    language: null,
    currency: null
  })

  // State - Preferences
  const preferences = ref({ ...EMPTY_PREFERENCES })

  // State - Premium/Subscription
  const isPremium = ref(false)

  // State - Loading & Error
  const isLoadingSettings = ref(false)
  const isLoadingPreferences = ref(false)
  const settingsError = ref(null)
  const preferencesError = ref(null)

  // Actions
  const fetchSettings = async () => {
    isLoadingSettings.value = true
    settingsError.value = null

    try {
      const data = await getSettings()
      // Normalize country code to uppercase (backend returns lowercase for new users)
      if (data.country) {
        data.country = data.country.toUpperCase()
      }
      settings.value = data
      return data
    } catch (error) {
      // 404 means new user - not an error, just empty state
      if (error.response?.status === 404) {
        console.log('Settings not found - new user')
        settingsError.value = null
        return null
      }

      console.error('Failed to fetch settings:', error)
      Sentry.captureException(error)
      settingsError.value = error.response?.data?.detail || 'Failed to load settings'
      throw error
    } finally {
      isLoadingSettings.value = false
    }
  }

  const fetchPreferences = async () => {
    isLoadingPreferences.value = true
    preferencesError.value = null

    try {
      const data = await getPreferences()
      const onboardingStore = useOnboardingStore()

      // Backend returns null for new users who haven't completed onboarding
      if (data === null) {
        console.log('Preferences are null - new user, onboarding not completed')
        preferences.value = { ...EMPTY_PREFERENCES }

        // Sync with onboarding store
        onboardingStore.setCompletionFromPreferences(null)

        return null
      }

      // User has preferences - onboarding completed
      preferences.value = data

      // Automatically populate and sync with onboarding store
      onboardingStore.populateFromPreferences(data)
      onboardingStore.setCompletionFromPreferences(data)

      return data
    } catch (error) {
      console.error('Failed to fetch preferences:', error)
      Sentry.captureException(error)
      preferencesError.value = error.response?.data?.detail || 'Failed to load preferences'
      throw error
    } finally {
      isLoadingPreferences.value = false
    }
  }

  // Fetch both settings and preferences
  const fetchUserData = async () => {
    try {
      await Promise.all([
        fetchSettings(),
        fetchPreferences()
      ])

      if (settings.value.id) {
        const { identify } = useCustomerIo();
        identify(settings.value.id);
        setSentryUser(settings.value.id);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error)
      Sentry.captureException(error)
      // Don't throw - individual fetch methods handle errors
    }
  }

  // Update settings locally (for reactivity)
  const updateSettings = (newSettings) => {
    settings.value = { ...settings.value, ...newSettings }
  }

  // Update preferences locally (for reactivity)
  const updatePreferences = (newPreferences) => {
    preferences.value = { ...preferences.value, ...newPreferences }
  }

  // Update premium status (called by useSubscription composable)
  const setPremiumStatus = (status) => {
    isPremium.value = status
  }

  // Reset user data (on logout)
  const reset = () => {
    const { reset: resetCustomerIo } = useCustomerIo();
    resetCustomerIo();
    clearSentryUser();
    settings.value = {
      username: null,
      id: null,
      email: null,
      country: null,
      oddsFormat: null,
      timezone: null,
      language: null,
      currency: null
    }
    preferences.value = { ...EMPTY_PREFERENCES }
    isPremium.value = false
    settingsError.value = null
    preferencesError.value = null
  }

  return {
    // State
    settings,
    preferences,
    isPremium,
    isLoadingSettings,
    isLoadingPreferences,
    settingsError,
    preferencesError,

    // Actions
    fetchSettings,
    fetchPreferences,
    fetchUserData,
    updateSettings,
    updatePreferences,
    setPremiumStatus,
    reset
  }
})
